import {
  Notice,
  Text,
  Actions,
  ButtonLink,
  IconRenderer,
  Stack,
  Inline,
  Toggle,
  Box,
  Alert
} from 'braid-design-system';
import { FileSvgIcon } from '@phosphor-icons/react';
import { ShowVerificationResultExample } from './components/VerifyWithSeekPassExamples.tsx';

import { ThemedExample } from '#src/components/ThemedExample/ThemedExample.tsx';
import {
  VerifyWithSeekPassButton,
  EmailExample
} from './components/VerifyWithSeekPassExamples';

# Verify with SEEK Pass

Verify with SEEK Pass allows a hirer to request to verify a candidate.
This is typically triggered when a candidate applies for a position.
Upon successful verification, the full legal name of the candidate and the verified badge will be displayed in the ATS.

Identity verification can be requested for any candidate.
It is not limited to candidates that have a SEEK Profile,
nor to those that found the position on a SEEK job board.

{/* IMPORTANT: Not linking to SEEK Pass documentation as it might cause confusion between integrating with SEEK API vs SEEK Pass direct integrations */}
{/* For more information on the verification process with SEEK Pass, see the [SEEK Pass documentation]. */}

## Before you begin

Ensure that you have reviewed the [initial setup for SEEK Profile](/use-cases/seek-profile#before-you-begin).

<Alert tone="caution">
  <Stack space="medium">
    <Text>
      This feature will be available to [Gold and Platinum] partners only at
      launch, with support for additional [credentials] and wider partner
      availability planned in future.
    </Text>
    <Text>
      Don’t commence your build until you receive confirmation that we can
      support the certification and release of your integration. We’ll get in
      touch with remaining partners once Verify with SEEK Pass reaches general
      availability.
    </Text>
  </Stack>
</Alert>

### Hirer onboarding

The candidate verification flow can only be triggered for hirers that have [onboarded] to this feature.
Hirers must have an established Verify with SEEK Pass relationship.

Your software can keep track of configured relationships based on onboarding requests and/or subscribing to the `HirerRelationshipChanged` event.
The [Developer Dashboard hirers page] displays the SEEK hirers that you have a relationship with and lets you check whether they have the required `CandidateVerification` relationship.
Alternatively, the [`hiringOrganization` query] enables your software to retrieve the relationships on demand.

### Hirer opt out

Your software must provide a way for hirers to opt out of sending Verify with SEEK Pass requests to candidates.
Verify with SEEK Pass is enabled by default for all hirers that have [onboarded] to this feature.

To check whether Verify with SEEK Pass is enabled for a given hirer, use the `hiringOrganization` query:

```graphql label="Query"
query ($id: String!) {
  hiringOrganization(id: $id) {
    id {
      value
    }
    name
    seekApiCapabilities {
      # If the disabledFeatures array is empty, Verify with SEEK Pass is enabled.
      # If the disabledFeatures array contains the code "CandidateVerification", Verify with SEEK Pass is disabled.
      disabledFeatures
    }
  }
}
```

```json label="Variables"
{
  "id": "seekAnzPublicTest:organization:seek:93WyyF1h"
}
```

```json label="Result with Verify with SEEK Pass enabled"
{
  "hiringOrganization": {
    "id": {
      "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
    },
    "name": "Acme Corp",
    "seekApiCapabilities": {
      "disabledFeatures": []
    }
  }
}
```

```json label="Result with Verify with SEEK Pass disabled"
{
  "hiringOrganization": {
    "id": "seekAnzPublicTest:organization:seek:93WyyF1h",
    "name": "Acme Corp",
    "seekApiCapabilities": {
      "disabledFeatures": ["CandidateVerification"]
    }
  }
}
```

To change whether Verify with SEEK Pass is enabled for a hirer, use the `setDisabledFeatures` mutation.
Pass the full list of feature codes you want disabled for that hirer; any feature not in the list remains enabled.

- **To disable** Verify with SEEK Pass, include `CandidateVerification` in the `disabledFeatures` array.
- **To enable** Verify with SEEK Pass, pass an empty array (or omit `CandidateVerification` from the list).

```graphql label="Mutation"
mutation ($input: SetDisabledFeaturesInput!) {
  setDisabledFeatures(input: $input) {
    disabledFeatures
  }
}
```

```jsonc label="Variables"
{
  "input": {
    "hiringOrganization": {
      "id": "seekAnzPublicTest:organization:seek:93WyyF1h"
    },
    // To disable:
    "disabledFeatures": ["CandidateVerification"]
    // To enable:
    // "disabledFeatures": []
  }
}
```

```json label="Result"
{
  "setDisabledFeatures": {
    "disabledFeatures": ["CandidateVerification"]
  }
}
```

Where this setting is placed depends on your software, though it is typically implemented as a checkbox on the hirer’s settings page.

<ThemedExample>
  <Stack space="large">
    <Inline>
      <Toggle
        on
        label={
          <>
            <span>Verify with SEEK Pass</span> <br />
            <small>
              Toggle this to control whether ID verification is shown against a
              candidate’s name and whether they receive ID verification request
              emails when they apply for a job
            </small>
          </>
        }
      />
    </Inline>
  </Stack>
</ThemedExample>

For hirers with multiple SEEK accounts, you must use the `hiringOrganization` query and `setDisabledFeatures` mutation (as documented above) for each of their SEEK accounts.

[`hiringOrganization` query]: /schema/query/hiringOrganization
[credentials]: https://seekpass.co/credentials
[Developer Dashboard hirers page]: /manage/hirers
[Gold and Platinum]: https://employer.seek.com/partners/connected-partners/
[onboarded]: /auth/hirer-relationships#hirer-onboarding

## Trigger verification process

Use the [`verifyCandidate` mutation] to initiate the verification process with SEEK Pass.
The candidate is matched using their **email address**; use the same email you use elsewhere with SEEK candidate APIs.

This mutation requires a [partner token](/auth/partner-tokens) to be triggered from your software’s backend.

Your software may automatically trigger verification for hirers when a job application is submitted,
or manually,
out of band of a job application process.

Only trigger this mutation for hirers that meet **both** of the following requirements:

- Have [onboarded](#hirer-onboarding) to Verify with SEEK Pass
- Have [enabled](#hirer-opt-out) Verify with SEEK Pass

The metadata included in the query variables enables enhanced user support through the ID verification process.

```graphql label="Mutation"
mutation ($input: VerifyCandidateInput!) {
  verifyCandidate(input: $input) {
    ... on VerifyCandidatePayload_Complete {
      verificationStatusCode
    }

    ... on VerifyCandidatePayload_HirerDisabled {
      verificationStatusCode
    }

    ... on VerifyCandidatePayload_Expired {
      verificationStatusCode
    }

    ... on VerifyCandidatePayload_Pending {
      externalId
      redirectUri

      button {
        label
        uri
        images {
          accent
          dark
          light
        }
      }
    }
  }
}
```

```jsonc label="Variables"
{
  "input": {
    // Required. The SEEK hirer ID of the `HiringOrganization` requesting candidate verification.
    "hiringOrganization": {
      "id": "seekAnzPublicTest:organization:seek:93WyyF1h"
    },

    // Required. The details of the candidate to verify.
    "candidate": {
      "email": "candidate@example.com"
    },

    // Required. The details of the position the candidate is applying for.
    "position": {
      // Required. Stable identifier of the position in your software.
      "seekPartnerPositionId": "1234567890",
      // Required. The title of the position.
      "positionTitle": "Software Engineer",
      // Required. The URI of the job ad.
      "positionUri": "https://www.partner.com/job/1234567890",
      // Required, if the job is posted on SEEK.
      "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV",
      // Optional. The location of the position.
      "positionLocation": "Sydney, Australia",
      // Optional. The job category of the position.
      "jobCategories": "Technology"
    }
  }
}
```

```jsonc label="Result (to be verified)"
{
  "verifyCandidate": {
    "verificationStatusCode": "PENDING",
    "externalId": "7cbc7e51-82f5-43ec-8d63-b7cdf1170425",
    "redirectUri": "https://app.seekpass.co/partner/verify/digitalIdentity?clientId=963266fbwtvbwrg34h35r&requestId=fa651300-b91a-49cb-8ee7-cac68b3fdf74",

    // Optional. The details for the button to display to the candidate in an email
    // or on a job application submission page in your software.
    "button": {
      "label": "Verify with SEEK Pass",
      "uri": "https://app.seekpass.co/partner/verify/digitalIdentity?clientId=963266fbwtvbwrg34h35r&requestId=fa651300-b91a-49cb-8ee7-cac68b3fdf74",
      "images": {
        "accent": "https://integration.seek.com/assets/verify-with-seek-pass/button-images/en/standard-accent.svg",
        "dark": "https://integration.seek.com/assets/verify-with-seek-pass/button-images/en/standard-dark.svg",
        "light": "https://integration.seek.com/assets/verify-with-seek-pass/button-images/en/standard-neutral.svg"
      }
    }
  }
}
```

```json label="Result (already verified)"
{
  "verifyCandidate": {
    "verificationStatusCode": "COMPLETE"
  }
}
```

A successful response will contain a `verificationStatusCode`:

- `COMPLETE` indicates that the candidate is already verified and has already consented to share their verification results

  No further action is necessary.

- `PENDING` indicates that the candidate has remaining actions to complete

  [Display a Verify with SEEK Pass button](#display-verify-button) and link the candidate to the `redirectUri` to complete their verification.
  This could be in an email, or on a job application submission page in your software.

  The `button` object provides:
  - `label`: The localised button text (e.g., “Verify with SEEK Pass”)
  - `uri`: The verification URL to navigate to when the button is clicked
  - `images`: Three SVG button variants (`accent`, `dark`, `light`) for different backgrounds

  If the verification process is triggered from the web, open the `redirectUri` in a new browser tab.
  It should be displayed _after_ the candidate has submitted their application.

If you receive a `FORBIDDEN` error,
your software may not have a Verify with SEEK Pass relationship with the hirer.

<Notice tone="info">
  <Text>
    Contact us if your software requires operations to verify candidates in
    bulk.
  </Text>
</Notice>

## Display verify button

Display the Verify with SEEK Pass button to allow candidates to verify their identity.
This should be displayed on an email sent from your software directly to the candidate.

<ThemedExample>
  <Box space="large" justifyContent="center" display="flex">
    <Inline>
      <VerifyWithSeekPassButton />
    </Inline>
  </Box>
</ThemedExample>

You may use the recommended copy below in candidate emails.
Ensure the button links the candidate to the `redirectUri` of the SEEK Pass identity verification flow.

<EmailExample />

### Emails

Follow these guidelines when adding the Verify with SEEK Pass button to email communications:

#### Important requirements

- The verification invitation email must be sent from the hirer (or ATS acting on the hirer’s behalf, where permitted) and not from SEEK or SEEK Pass.
  The verification email must clearly identify the hirer as the sender of that email.
- Use hirer branding (e.g. logo, colours) and send from a hirer-owned address such as `careers@example.com`.
- Candidates should clearly recognise the email as a request from the employer they applied to.
- Hirers must be able to customise the content of the verification email.
  Your software should support editable copy (e.g. greeting, body text, sign-off) so each hirer can tailor the message.
  The section that explains Verify with SEEK Pass and contains the verification button must remain fixed.
  Hirers cannot change or remove this block.
  Hirers must not include language that promotes SEEK Pass beyond the immediate application context.
- Optionally, if you do include an unsubscribe mechanism, it should operate on a per-hirer basis.
  For example, a candidate opting out of verification requests from one hirer does not affect their applications with other hirers.
  The candidate should also be provided with a mechanism to resubscribe to receiving verification invites from that hirer.

#### Use PNG format for compatibility

PNG button assets provide the best support across email clients.
Avoid using SVG or other formats that may not render consistently.

#### Set appropriate dimensions

The PNG buttons are exported at 2x resolution for rendering on high-resolution displays.
Set the `width` attribute to **half** the original size of the exported button.

```html
<!-- The exported button is 618px by 96px (@2x) -->
<img
  src="cid:vwsp-en-standard-accent.png"
  width="309"
  alt="Verify with SEEK Pass"
/>
```

#### Embed images inline

Many email clients block external images by default.
To ensure your button displays immediately, attach the image as an inline resource using Content-ID (CID) embedding rather than linking to an external URL.

This requires creating a multipart email with the image attached and referenced via the `cid:` scheme.
The attachment should include these headers:

```http
Content-Type: image/png; name=vwsp-en-standard-accent.png
Content-ID: <vwsp-en-standard-accent.png>
Content-Transfer-Encoding: base64
Content-Disposition: inline; filename="vwsp-en-standard-accent.png"

<base64 image data>
```

Note: The `Content-ID` value (excluding angle brackets) must match the filename referenced in your `src="cid:..."` attribute.

## Display verification result

To display verification results within a candidate or applicant list,
follow the steps on [the next page](/use-cases/seek-profile/summary) along with this guide,
as both SEEK Profile and identity verification results should be retrieved from the same query.

Use the response from the [`seekCandidateSummaries` query] to determine whether to show a verification result for each candidate:

- If `identity.isVerified` is `true`, render the verification result
- If `identity.isVerified` is `false`, do not render the verification result

Identity verification must conform to our display requirements:

1. Render the SEEK Pass badge to denote the verification result

   Your software must not modify it.

2. Surface the `formattedName` alongside the badge

   This allows hiring managers to easily verify that the verification was performed on the correct candidate.

### Design guidance

- Display the SEEK Pass icon next to the candidate’s name.
  Retrieve the icon and its tooltip from the `identity.icon` field of the [`seekCandidateSummaries` query].
- Display a tooltip when hovering over the icon.
  If the response `identity.icon.tooltipLabel` cannot be used, the tooltip copy should be: `Verified by SEEK Pass as {formattedName}`.
- Include a legend on your page with a badge that explains the meaning of the SEEK Pass icon.

<ShowVerificationResultExample />

## Data retention

Verification results remain available as long as the candidate permits based on their SEEK Profile privacy setting.

The result will become unavailable if the candidate deletes their SEEK Pass account.

Verification results may be available to other hirers for candidates that consent to share results to their public profile.

<Notice tone="info">
  <Text>
    Contact us if your software requires webhook notifications for verification
    results.
  </Text>
</Notice>

## Assets

SEEK provides a bundle of Verify with SEEK Pass button assets, including pre-approved buttons, text, and logo assets.
You can always find the latest version below.

<Notice tone="info">
  <Stack space="small">
    <Text weight="medium">Use PNG buttons in emails</Text>
    <Text>
      Use a PNG button asset in emails as SVGs may not render consistently or at
      all in certain email clients.
    </Text>
  </Stack>
</Notice>

<Actions>
  <ButtonLink
    href="https://integration.seek.com/assets/verify-with-seek-pass.zip"
    icon={
      <IconRenderer>
        {({ className }) => <FileSvgIcon className={className} />}
      </IconRenderer>
    }
    download
  >
    Button assets
  </ButtonLink>
</Actions>
<Text size="small">Last updated: 2026-01-20</Text>

[`verifyCandidate` mutation]: /schema/mutation/verifyCandidate
[`seekCandidateSummaries` query]: /use-cases/seek-profile/summary#retrieve-summaries
